import { useManager } from 'dreamstate';

import { Flex, rem } from '@vimeo/bokeh';
import { Chats } from '@vimeo/bokeh-icons';
import { createInteractionDomName } from '@vimeo/cloud-live/libs/shared/utils/dom/identifying';
import { translations } from '@vimeo/cloud-live/translations';
import { IBaseDomElementProps } from '@vimeo/core/types/dom';
import { Maybe } from '@vimeo/core/types/generic';
import { ChatHistory } from '@vimeo/live-core/components/interaction/chat/chat_history/ChatHistory';
import { ChatInput } from '@vimeo/live-core/components/interaction/chat/ChatInput';
import { ChatPreloader } from '@vimeo/live-core/components/interaction/chat/ChatPreloader';
import { EmptyStatePlaceholder } from '@vimeo/live-core/components/panels/empty';
import { useLiveGlobals } from '@vimeo/live-core/components/providers/globals';
import { IFirebaseContext, InteractionFirebaseManager } from '@vimeo/live-core/store/firebase/base';
import { ChatManager, IChatContext } from '@vimeo/live-core/store/firebase/interaction/chat';
import { EChatType } from '@vimeo/live-core/types/interaction_tools';
import { IInteractionStoreInitialState } from '@vimeo/live-core/types/store';

interface IChatViewProps extends IBaseDomElementProps {
  chatType: EChatType;
  firebaseContext?: IFirebaseContext;
  chatContext?: IChatContext;
}

export function ChatView({
  id = createInteractionDomName('chat-view'),
  className = createInteractionDomName('chat-view'),
  chatType,
  firebaseContext: {
    interactionCredentials,
    connectionStatuses: { isInteractionConnected },
    // eslint-disable-next-line react-hooks/rules-of-hooks
  } = useManager(InteractionFirebaseManager, ({ interactionCredentials }) => [
    interactionCredentials,
  ]),
  chatContext: {
    isEnabled,
    // eslint-disable-next-line react-hooks/rules-of-hooks
  } = useManager(ChatManager),
}: IChatViewProps) {
  const { initialState } = useLiveGlobals<IInteractionStoreInitialState>();

  const currentUserId: Maybe<string> = interactionCredentials?.user?.id;

  return (
    <Flex id={id} flexGrow={1} flexDirection={'column'} overflow={'hidden'} paddingTop={rem(4)}>
      {!currentUserId ? (
        <ChatPreloader id={createInteractionDomName(id, 'preloader')} />
      ) : isEnabled ? (
        <Flex
          id={createInteractionDomName(id, 'content')}
          flexDirection={'column'}
          flexGrow={1}
          height={'100%'}
          padding={`0 ${rem(16)} ${rem(16)} ${rem(16)}`}
        >
          <ChatHistory
            chatType={chatType}
            currentUserId={currentUserId}
            scrollBackground={initialState?.interaction?.backgroundColor || 'background'}
            placeholder={
              <EmptyStatePlaceholder
                id={createInteractionDomName(id, 'chat-history-placeholder')}
                icon={<Chats boxSize={'lg'} />}
                description={translations.sendMessageToStartChat}
                control={null}
              />
            }
          />

          <ChatInput
            chatType={chatType}
            isDisabled={!isInteractionConnected}
            placeholderText={translations.sendMessage}
          />
        </Flex>
      ) : (
        <EmptyStatePlaceholder
          id={createInteractionDomName(id, 'placeholder')}
          className={createInteractionDomName(className, 'placeholder')}
          icon={<Chats boxSize={'lg'} />}
          control={null}
        />
      )}
    </Flex>
  );
}
